// The dots for the halftone dissolve: each dot of a picture shrinks to nothing, like the
// portrait's dots do. (halftone-layer.ts draws them.)

import { clamp01, smoothstep } from "@bfly/engine/math";

// The fraction of a square cell that a dot of radius `r` (in half cells) covers. It's a circle
// until it hits the cell's sides, then a circle with its four sides cut off.

function covered(r: number): number {
  if (r <= 1) return (Math.PI * r * r) / 4;
  const cut = r * r * Math.acos(1 / r) - Math.sqrt(r * r - 1);
  return Math.min(1, (Math.PI * r * r - 4 * cut) / 4);
}

// How much of the cell a circle touching its sides covers.
const SIDES = Math.PI / 4;

// Past the cell's sides, the area has no simple inverse, so halve the range until it's close
// enough.
function solve(cover: number): number {
  let low = 1;
  let high = Math.SQRT2;
  for (let step = 0; step < 30; step++) {
    const middle = (low + high) / 2;
    if (covered(middle) < cover) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

// The radius past the cell's sides, at even steps of coverage from `SIDES` to 1. It's built
// once, since the dissolve sizes every dot on every scroll.
const STEPS = 64;
const TABLE = Float64Array.from({ length: STEPS + 1 }, (_, i) =>
  solve(SIDES + ((1 - SIDES) * i) / STEPS),
);

/** A dot's radius at a dissolve `amount` (0 to 1), as a fraction of half its cell. It's the
    radius that covers `1 - amount` of the cell, so the picture's covered area drops evenly
    over the whole dissolve, which reads as long and gradual. At 0 it's the square root of 2:
    the dots reach the cells' corners and the picture is whole. */
export function halftoneDot(amount: number): number {
  const cover = 1 - clamp01(amount);
  // Up to a full circle in the cell, the area has a simple inverse.
  if (cover <= SIDES) return Math.sqrt((4 * cover) / Math.PI);
  const at = ((cover - SIDES) / (1 - SIDES)) * STEPS;
  const i = Math.min(STEPS - 1, Math.floor(at));
  const low = TABLE[i] ?? 1;
  const high = TABLE[i + 1] ?? Math.SQRT2;
  return low + (high - low) * (at - i);
}

// A dot stays opaque for this fraction of its dissolve, then fades, the same as a word does
// (`DISSOLVE_FADE` in text-effects.ts).
const FADE_FROM = 0.4;

/** A dot's opacity at a dissolve `amount` (0 to 1): fully opaque at first, then easing down to
    nothing at the end, so the dots fade with the text around them instead of staying solid
    until the end. */
export function dotOpacity(amount: number): number {
  return 1 - smoothstep(0, 1, (amount - FADE_FROM) / (1 - FADE_FROM));
}
